import { all, takeEvery, fork, put, select, call } from 'redux-saga/effects';
import { whoAmIBackground, executeErrorCode } from '../../auth/actions';
import SubjectsCardService from '../../../services/subjectsCards';
import FormsCardService from '../../../services/forms';
import { kartaPrzedmiotuActionTypes, kartaPrzedmiotuActions, kartaPrzedmiotuActionsInterfaces } from '.';
import { isTabWithForm } from '../helpers';
import { dynamicTabsActions } from '..';
import UrlBuilder from '../../../helpers/urlBuilder';
import {
  getColumnsConfig as getColumnsConfigAction,
  refreshData as subjectsCardsRefreshDataAction,
} from '../../staticViews/subjectsCards/actions';
import { getColumnsConfig as getColumnsConfigSaga } from '../../staticViews/subjectsCards/saga';
import { getStaticViewState } from '../../staticViews/selectors';
import { getTabData, getDynamicTabListFromStore } from '../selectors';
import utilityActions from '../../utility/actions';
import { NotificationTypes } from '../../utility/types';

const { showNotification } = utilityActions;

export function* initKartaPrzedmiotuWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.INIT, initKartaPrzedmiotu);
}
/**
 * inicjalizacja pobierania danych o przedmiocie, którego dotyczy zakładka
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* initKartaPrzedmiotu(action: kartaPrzedmiotuActionsInterfaces.Init): Generator {
  const { tabId } = action.payload;
  yield call(getLeftPanelData, kartaPrzedmiotuActions.getLeftPanelData(tabId));
  yield put(dynamicTabsActions.initFinish(tabId));
}

export function* getLeftPanelDataWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.GET_LEFT_PANEL_DATA_START, getLeftPanelData);
}
/**
 * pobieranie danych o przedmiocie, którego dotyczy zakładka
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getLeftPanelData(action: kartaPrzedmiotuActionsInterfaces.GetLeftPanelData): Generator {
  const { tabId } = action.payload;
  try {
    const { loading, data, columns } = (yield select(state => getStaticViewState(state, 'SubjectsCards'))) as ViewDataSet;
    /*
      Pobieranie configu kolumn dla "SubjectsCards".
      Konfiguracja kolumn jest niezbędna do wyświetlania danych o karcie po lewej stronie karty przedmiotu
      (SubjectCard).
      - uruchamiany, jeśli nie jest on dostępny w redux staticViews -> SubjectsCards
    */
    yield all([columns.length === 0 && !loading ? call(getColumnsConfigSaga, getColumnsConfigAction('SubjectsCards')) : null]);
    yield put(dynamicTabsActions.setLeftPanelLoading(tabId));
    const tabData = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
    const recordFromList = data.find(record => record.przedmiotId.toString() === tabData.itemId);
    if (recordFromList) {
      yield put(dynamicTabsActions.getLeftPanelDataSuccess(tabId, recordFromList, recordFromList.przedmiotNazwa));
    }
    yield put(kartaPrzedmiotuActions.fetchCardData(tabId));
  } catch (error) {
    yield put(dynamicTabsActions.getLeftPanelDataFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* fetchCardDataWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FETCH_CARD_DATA_START, fetchCardData);
}
/**
 * Pobieranie danych o przedmiocie, którego dotyczy zakładka
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* fetchCardData(action: kartaPrzedmiotuActionsInterfaces.FetchCardData): Generator {
  const { tabId } = action.payload;
  try {
    const { columns } = (yield select(state => getStaticViewState(state, 'SubjectsCards'))) as ViewDataSet;
    const { itemId } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
    const response: any = yield call(SubjectsCardService.getSubjectCardDetails, {
      filter: {
        przedmiotId: itemId,
      },
      columns: columns.map(column => column.dataIndex),
    });
    yield put(
      dynamicTabsActions.getLeftPanelDataSuccess(
        tabId,
        response.kartaPrezdmiotuListaElementPageDto.data[0],
        response.kartaPrezdmiotuListaElementPageDto.data[0].przedmiotNazwa,
      ),
    );
  } catch (error) {
    yield put(kartaPrzedmiotuActions.fetchCardDataFail(tabId));
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* getFormTemplateWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FORM_TEMPLATE_GET_START, getFormTemplate);
}
/**
 * Pobieranie szablonu karty przedmiotu. Szablon pobierany jest na podstawie wersji szablonu.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getFormTemplate(action: kartaPrzedmiotuActionsInterfaces.GetFormTemplate): Generator {
  const { tabId } = action.payload;
  try {
    const { form } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
    if (form.templateVersion) {
      const { forms }: any = yield FormsCardService.getFormTemplate(form.templateVersion);
      yield put(kartaPrzedmiotuActions.getFormTemplateSuccess(tabId, forms));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(kartaPrzedmiotuActions.getFormTemplateFail(tabId));
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* getFormDataWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FORM_DATA_GET_START, getFormData);
}
/**
 * pobieranie danych do formularza karty przedmiotu
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getFormData(action: kartaPrzedmiotuActionsInterfaces.GetFormData): Generator {
  const { tabId } = action.payload;
  const { form, itemId } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
  try {
    if (form.templateVersion) {
      const { data }: any = yield FormsCardService.getFormData(form.templateVersion, itemId);
      yield put(kartaPrzedmiotuActions.getFormDataSuccess(tabId, data));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(kartaPrzedmiotuActions.getFormDataFail(tabId));
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

// Zapis danych formularza
export function* saveFormDataWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FORM_DATA_SAVE_START, saveFormData);
}
/**
 * Zapisywanie karty przedmiotu w tabeli tymczasowej.
 * Zanim karta przedmiotu wraz z efektami uczenia się zostanie opublikowana w SID, zostaje zapisana w tzw. tabeli tymczasowej w bazie danych.
 * Zapis karty w tabeli tymczasowej następuje wraz z wciśnięciem przycisku "Zapisz" na edytowanej karcie przedmiotu.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* saveFormData(action: kartaPrzedmiotuActionsInterfaces.SaveFormData): Generator {
  const { tabId, readOnly } = action.payload;
  try {
    /* dla karty przedmiotu jako itemId przechowywane jest id przedmiotu (przedmiotId) */
    const { form, itemId } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
    const { formValues, templateVersion } = form;
    if (templateVersion) {
      yield call(FormsCardService.saveFormData, templateVersion, itemId, formValues);
      yield put(kartaPrzedmiotuActions.saveFormDataSuccess(tabId, readOnly));
      yield put(kartaPrzedmiotuActions.getFormData(tabId));
      yield put(kartaPrzedmiotuActions.getFormTemplate(tabId));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(kartaPrzedmiotuActions.saveFormDataFail(tabId));
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getFormForVersionWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FORM_TEMPLATE_SET_VERSION, getFormForVersion);
}
/**
 * Pobranie danych oraz template dla karty przedmiotu po wybraniu wersji.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getFormForVersion(action: kartaPrzedmiotuActionsInterfaces.SetFormTemplateVersion): Generator {
  const { tabId } = action.payload;
  yield all([put(kartaPrzedmiotuActions.getFormTemplate(tabId)), put(kartaPrzedmiotuActions.getFormData(tabId))]);
}
export function* setEditModeWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FORM_TOGGLE_EDIT_MODE, setEditMode);
}
/**
 * Uruchomienie przedłużenia tokenu podczas, gdy użytkownik przełącza kartę przedmiotu w tryb edycji.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* setEditMode(action: kartaPrzedmiotuActionsInterfaces.ToggleEditMode): Generator {
  const tabList = (yield select(getDynamicTabListFromStore)) as TabList;
  const { tabId } = action.payload;
  const tab = tabList.find(tab => tab.id === tabId);
  if (tab && isTabWithForm(tab) && !tab.form.readOnly) {
    yield put(whoAmIBackground());
  }
}
export function* initPublishingWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.INIT_PUBLISHING, initPublishing);
}
/**
 * Inicjalizacja publikacji karty przedmiotu i efektów kierunkowych w ZTN.
 * Publikacja następuje po kliknięciu przycisku "Zapisz kartę w SID" na karcie przedmiotu.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* initPublishing(action: kartaPrzedmiotuActionsInterfaces.InitPublishing): Generator {
  const { tabId } = action.payload;
  try {
    const { form, leftPanel } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
    const wersjaSzablonuKarty = form.templateVersion;
    const { przedmiotId } = leftPanel.data;
    if (wersjaSzablonuKarty) {
      /*
        Czekamy, aż zostaną opublikowane karta przedmiotów oraz efekty kierunkowe.
        Do publikacji obu elementów wysyłane są 2 osobne requesty z innymi parametrami oraz inaczej obsługiwana jest odpowiedź "success",
        dlatego akcje są rozdzielone.
      */
      yield all([
        call(
          publishLearningEffectsInSID,
          kartaPrzedmiotuActions.publishLearningEffectsInSID(tabId, wersjaSzablonuKarty, przedmiotId),
        ),
        call(publishKartaInSID, kartaPrzedmiotuActions.publishCardInSID(tabId, wersjaSzablonuKarty, przedmiotId)),
      ]);
    }
    // todo - jakas obsluga gdy wersjaSzablonuKarty bedzie undefined
    yield put(kartaPrzedmiotuActions.init(tabId));
    yield put(kartaPrzedmiotuActions.getFormTemplate(tabId));
    yield put(kartaPrzedmiotuActions.getFormData(tabId));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

/**
 * publikacja karty przedmiotu w ZTN
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* publishKartaInSID(action: kartaPrzedmiotuActionsInterfaces.PublishCardInSID): Generator {
  const { wersjaSzablonuKarty, przedmiotId } = action.payload;
  try {
    yield call(FormsCardService.saveInSID, UrlBuilder.generateFilterParam({ wersjaSzablonuKarty, przedmiotId }));
    yield put(
      showNotification({
        notifType: 'success' as NotificationTypes,
        titleTranslationKey: 'SUBJECT_CARD_SAVE_TO_SID_SUCCESS_TITLE',
        bodyTranslationKey: '',
        duration: 10,
      }),
    );
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

/**
 * publikacja efektów kierunkowych w ZTN
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* publishLearningEffectsInSID(action: kartaPrzedmiotuActionsInterfaces.PublishLearningEffectsInSID): Generator {
  const { przedmiotId, wersjaSzablonuKarty, tabId } = action.payload;
  try {
    yield call(SubjectsCardService.publishLearningEffects, {
      przedmiotId,
      wersjaKarty: wersjaSzablonuKarty,
    });
    yield put(
      showNotification({
        notifType: 'success' as NotificationTypes,
        titleTranslationKey: 'SUBJECT_CARD_PUBLISH_LEARNING_EFFECTS_SUCCESS_TITLE',
        bodyTranslationKey: '',
        duration: 10,
      }),
    );
    /*
      Po publikacji efektów kierunkowych odświeżamy listę kart przedmiotów oraz szczegóły karty,
      aby zaktualizować status na liście kart oraz na karcie przedmiotu.
    */
    yield put(subjectsCardsRefreshDataAction('SubjectsCards'));
    yield put(kartaPrzedmiotuActions.fetchCardData(tabId));
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}
export function* updateEditModeStatusWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.FORM_UPDATE_EDIT_MODE_STATUS, updateEditModeStatus);
}
/**
 *  Wysłanie requestu o aktualizację statusu trybu edycji karty przedmiotu w bazie danych.
 *  Informacja o statusie edycji karty jest przechowywana w bazie danych, dlatego request jest wysyłany,
 *  gdy użytkownik kliknie przycisk "Edytuj" na karcie przedmiotu.
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* updateEditModeStatus(action: kartaPrzedmiotuActionsInterfaces.UpdateEditModeStatus): Generator {
  const { tabId } = action.payload;
  yield put(kartaPrzedmiotuActions.saveFormData(tabId, false));
  yield put(kartaPrzedmiotuActions.toggleEditMode(tabId));
}

export function* getCardFromSidWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.GET_CARD_FROM_SID_START, getCardFromSid);
}
/**
 * Pobieranie karty w SID
 * Spowoduje nadpisanie obecnej karty i w razie poprawnej odpowiedzi odświeży stronę
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getCardFromSid(action: kartaPrzedmiotuActionsInterfaces.GetCardFromSid): Generator {
  const { tabId, readOnly } = action.payload;
  try {
    /* dla karty przedmiotu jako itemId przechowywane jest id przedmiotu (przedmiotId) */
    const { form, itemId } = (yield select(state => getTabData(state, tabId))) as DynamicTabWithForm<SubjectCardData>;
    const { templateVersion } = form;
    if (templateVersion) {
      yield call(FormsCardService.getCardFromSid, templateVersion, itemId);
      yield put(kartaPrzedmiotuActions.getCardFromSidSuccess(tabId, readOnly));
    }
  } catch (error) {
    if (error && error.errorCode) {
      yield put(kartaPrzedmiotuActions.getCardFromSidFail(tabId));
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export function* getCardFromSidAfterSuccessWatcher(): Generator {
  yield takeEvery(kartaPrzedmiotuActionTypes.GET_CARD_FROM_SID_SUCCESS, getCardFromSidAfterSuccess);
}
/**
 * Akcja success w momencie kiedy pobieranie danych z SID powiedzie się pomyślnie
 *
 * @export
 * @param {AnyAction} action
 * @return {*}  {Generator}
 */
export function* getCardFromSidAfterSuccess(action: kartaPrzedmiotuActionsInterfaces.GetCardFromSidSuccess): Generator {
  const { tabId } = action.payload;
  try {
    yield put(kartaPrzedmiotuActions.getFormData(tabId));
    yield put(kartaPrzedmiotuActions.getFormTemplate(tabId));
    yield put(
      showNotification({
        notifType: 'success' as NotificationTypes,
        titleTranslationKey: 'BUTTON_GET_CARD_FROM_SID_SUCCESS',
        bodyTranslationKey: '',
        duration: 10,
      }),
    );
  } catch (error) {
    if (error && error.errorCode) {
      yield put(executeErrorCode(error.errorCode, action, error));
    }
  }
}

export default function* rootSaga(): Generator {
  yield all([
    fork(initKartaPrzedmiotuWatcher),
    fork(getLeftPanelDataWatcher),
    fork(fetchCardDataWatcher),
    fork(getFormTemplateWatcher),
    fork(getFormDataWatcher),
    fork(saveFormDataWatcher),
    fork(getFormForVersionWatcher),
    fork(setEditModeWatcher),
    fork(initPublishingWatcher),
    fork(updateEditModeStatusWatcher),
    fork(getCardFromSidWatcher),
    fork(getCardFromSidAfterSuccessWatcher),
  ]);
}
